import { useEffect, useState, useMemo } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Button,
  Table,
  Modal,
  Descriptions,
  Space,
  DatePicker,
  message,
  Spin,
  Tooltip,
  Typography,
  Dropdown,
  Menu,
} from 'antd';
import {
  ShoppingCartOutlined,
  EuroOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  EyeOutlined,
  ReloadOutlined,
  CalendarOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PlacesAutocomplete from '../components/PlacesAutocomplete';
import { useOrderProducts } from '../hooks/useOrderProducts';
import { formatPrice, formatNumber } from '../utils/format';

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Title } = Typography;

interface Metrics {
  totalOrders: number;
  totalRevenue: number;
  activeOrders: number;
  totalProducts: number;
  recentOrders: Array<{
    id: string;
    aangemaakt_op: string;
    status: string;
    totaal_bedrag: number;
  }>;
}

interface DailyOrderStats {
  date: string;
  count: number;
  total: number;
  tooltipText: string;
}

interface VerpactData {
  materiaal_naam: string;
  verpact_bijdrage: number;
  percentage: number;
}

// Define colors for the pie chart
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B6B', '#6BD4FF', '#8FD14F'];

const statusColors = {
  nieuw: 'blue',
  in_behandeling: 'orange',
  verzonden: 'purple',
  afgeleverd: 'green',
  geannuleerd: 'red',
};

const statusLabels = {
  nieuw: 'Nieuw',
  in_behandeling: 'In behandeling',
  verzonden: 'Verzonden',
  afgeleverd: 'Afgeleverd',
  geannuleerd: 'Geannuleerd',
};

export default function Dashboard() {
  const { session } = useAuth();
  const [metrics, setMetrics] = useState<Metrics>({
    totalOrders: 0,
    totalRevenue: 0,
    activeOrders: 0,
    totalProducts: 0,
    recentOrders: [],
  });
  const [loading, setLoading] = useState(true);
  const [verpactLoading, setVerpactLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ]);
  const [activeDateRange, setActiveDateRange] = useState<string>('last30Days');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { orderProducts: selectedOrderProducts, loading: productsLoading } = useOrderProducts(selectedOrder?.id || '');
  const [verpactData, setVerpactData] = useState<VerpactData[]>([]);
  const [totalVerpactBijdrage, setTotalVerpactBijdrage] = useState<number>(0);

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch metrics data
  const fetchMetrics = async () => {
    setLoading(true);
    try {
      console.log('Fetching metrics...');
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          id,
          status,
          totaal_bedrag,
          aangemaakt_op
        `)
        .eq('klant_id', session?.user.id)
        .gte('aangemaakt_op', dateRange[0].startOf('day').toISOString())
        .lte('aangemaakt_op', dateRange[1].endOf('day').toISOString())
        .order('aangemaakt_op', { ascending: false });

      if (orderError) {
        console.error('Error fetching orders:', orderError);
        throw orderError;
      }

      console.log('Fetched orders:', orderData);

      // Fetch product count directly from product_access
      const { data: productAccess, error: productError } = await supabase
        .from('product_access')
        .select('id')
        .eq('user_id', session?.user.id);

      if (productError) {
        console.error('Error fetching products:', productError);
      }

      const totalProducts = productAccess?.length || 0;
      console.log('User has access to', totalProducts, 'products');

      const totalOrders = orderData?.length || 0;
      const totalRevenue = orderData?.reduce((sum, order) => sum + (Number(order.totaal_bedrag) || 0), 0) || 0;
      const activeOrders = orderData?.filter(order => order.status !== 'verzonden').length || 0;

      console.log('Calculated metrics:', {
        totalOrders,
        totalRevenue,
        activeOrders,
        totalProducts,
        recentOrders: orderData
      });

      setMetrics({
        totalOrders,
        totalRevenue,
        activeOrders,
        totalProducts,
        recentOrders: orderData || [],
      });
    } catch (error) {
      console.error('Error in fetchMetrics:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de gegevens.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Verpact data for the customer
  const fetchVerpactData = async () => {
    setVerpactLoading(true);
    try {
      // First get all orders within the date range
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('id')
        .eq('klant_id', session?.user.id)
        .gte('aangemaakt_op', dateRange[0].startOf('day').toISOString())
        .lte('aangemaakt_op', dateRange[1].endOf('day').toISOString());

      if (orderError) {
        console.error('Error fetching orders for Verpact data:', orderError);
        throw orderError;
      }

      if (!orderData || orderData.length === 0) {
        console.log('No orders found in the selected date range');
        setVerpactData([]);
        setTotalVerpactBijdrage(0);
        setVerpactLoading(false);
        return;
      }

      // Get all order IDs
      const orderIds = orderData.map(order => order.id);
      
      // Fetch order products for these orders
      const { data: orderProductsData, error: orderProductsError } = await supabase
        .from('order_products')
        .select('product_id')
        .in('order_id', orderIds);
        
      if (orderProductsError) {
        console.error('Error fetching order products:', orderProductsError);
        throw orderProductsError;
      }
      
      if (!orderProductsData || orderProductsData.length === 0) {
        console.log('No products found in orders for the selected date range');
        setVerpactData([]);
        setTotalVerpactBijdrage(0);
        setVerpactLoading(false);
        return;
      }
      
      // Extract unique product IDs
      const productIdsSet = new Set(orderProductsData.map(item => item.product_id));
      const productIds = Array.from(productIdsSet);
      
      console.log(`Found ${productIds.length} unique products in orders for the selected date range`);
      
      // For each product, get the materials data
      let allMaterialsData: any[] = [];
      let totalBijdrage = 0;
      
      // We'll use Promise.all to fetch all the material data in parallel
      const promises = productIds.map(async (productId) => {
        const { data, error } = await supabase
          .rpc('get_product_materialen', { product_id: productId });

        if (error) {
          console.error(`Error fetching materials for product ${productId}:`, error);
          return [];
        }
        
        return data || [];
      });

      const results = await Promise.all(promises);
      
      // Combine all materials data
      results.forEach(materials => {
        allMaterialsData = [...allMaterialsData, ...materials];
      });

      // Calculate total bijdrage
      totalBijdrage = allMaterialsData.reduce((sum, material) => 
        sum + Number(material.verpact_bijdrage), 0);

      // Aggregate by material name
      const materialSummary: Record<string, number> = {};
      allMaterialsData.forEach(material => {
        const materialName = material.materiaal_naam;
        if (!materialSummary[materialName]) {
          materialSummary[materialName] = 0;
        }
        materialSummary[materialName] += Number(material.verpact_bijdrage);
      });

      // Convert to array and calculate percentages
      const verpactChartData = Object.entries(materialSummary).map(([name, value]) => ({
        materiaal_naam: name,
        verpact_bijdrage: value,
        percentage: totalBijdrage > 0 ? (value / totalBijdrage) * 100 : 0
      }));

      // Sort by bijdrage value (highest first)
      verpactChartData.sort((a, b) => b.verpact_bijdrage - a.verpact_bijdrage);

      // Limit to top 7 materials and group the rest as "Overig"
      if (verpactChartData.length > 7) {
        const topMaterials = verpactChartData.slice(0, 7);
        const otherMaterials = verpactChartData.slice(7);
        
        const otherBijdrage = otherMaterials.reduce((sum, material) => 
          sum + material.verpact_bijdrage, 0);
        
        const otherPercentage = totalBijdrage > 0 ? (otherBijdrage / totalBijdrage) * 100 : 0;
        
        topMaterials.push({
          materiaal_naam: 'Overig',
          verpact_bijdrage: otherBijdrage,
          percentage: otherPercentage
        });
        
        setVerpactData(topMaterials);
      } else {
        setVerpactData(verpactChartData);
      }
      
      setTotalVerpactBijdrage(totalBijdrage);
      
    } catch (error) {
      console.error('Error fetching Verpact data:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de Verpact gegevens.');
    } finally {
      setVerpactLoading(false);
    }
  };

  // Process data for the graph in a simpler format
  const dailyStats = useMemo(() => {
    if (!metrics.recentOrders?.length) {
      console.log('No orders data available');
      return [];
    }

    const startDate = dateRange[0].startOf('day');
    const endDate = dateRange[1].endOf('day');
    
    // Create a map for all dates in range
    const dateMap: Record<string, DailyOrderStats> = {};
    let current = startDate.clone();
    
    // Ensure we have entries for all dates in the range
    while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
      const dateKey = current.format('DD-MM');
      dateMap[dateKey] = {
        date: dateKey,
        count: 0,
        total: 0,
        tooltipText: '0 orders (€ 0,00)'
      };
      current = current.add(1, 'day');
    }

    // Fill in order data
    metrics.recentOrders.forEach(order => {
      const orderDate = new Date(order.aangemaakt_op);
      const dateKey = orderDate.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
      
      if (dateMap[dateKey]) {
        dateMap[dateKey].count += 1;
        dateMap[dateKey].total += Number(order.totaal_bedrag) || 0;
        dateMap[dateKey].tooltipText = `${dateMap[dateKey].count} orders (${formatPrice(dateMap[dateKey].total)})`;
      }
    });

    // Convert to array and sort by date
    const result = Object.values(dateMap).sort((a, b) => {
      const [dayA, monthA] = a.date.split('-').map(Number);
      const [dayB, monthB] = b.date.split('-').map(Number);
      
      if (monthA !== monthB) return monthA - monthB;
      return dayA - dayB;
    });

    console.log('Recharts data:', result);
    return result;
  }, [metrics.recentOrders, dateRange]);

  // Debug log to check data
  useEffect(() => {
    console.log('Daily Stats:', dailyStats);
  }, [dailyStats]);

  useEffect(() => {
    if (session?.user) {
      fetchMetrics();
      fetchVerpactData();
    }
  }, [session, dateRange]);

  // Handle predefined date range selection
  const handleDateRangeChange = (range: string) => {
    setActiveDateRange(range);
    
    let start: dayjs.Dayjs;
    let end: dayjs.Dayjs = dayjs();
    
    switch (range) {
      case 'today':
        start = dayjs().startOf('day');
        break;
      case 'last7Days':
        start = dayjs().subtract(7, 'days');
        break;
      case 'last30Days':
        start = dayjs().subtract(30, 'days');
        break;
      case 'thisMonth':
        start = dayjs().startOf('month');
        break;
      case 'lastMonth':
        start = dayjs().subtract(1, 'month').startOf('month');
        end = dayjs().subtract(1, 'month').endOf('month');
        break;
      default:
        start = dayjs().subtract(30, 'days');
    }
    
    setDateRange([start, end]);
  };
  
  // Reset to default date range (last 30 days)
  const resetDateRange = () => {
    handleDateRangeChange('last30Days');
  };
  
  // Handle custom date range selection from RangePicker
  const handleCustomDateRange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
      setActiveDateRange('custom');
    }
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      width: '15%',
      responsive: ['md'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status: keyof typeof statusColors) => (
        <Tag color={statusColors[status] || 'default'}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Bedrag',
      dataIndex: 'totaal_bedrag',
      key: 'totaal_bedrag',
      width: '15%',
      render: (amount: number) => 
        new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount),
      responsive: ['sm'],
    },
    {
      title: 'Datum',
      dataIndex: 'aangemaakt_op',
      key: 'aangemaakt_op',
      width: '15%',
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
    },
    {
      title: 'Acties',
      key: 'actions',
      width: '10%',
      render: (record: any) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => setSelectedOrder(record)}
          size={isMobile ? 'small' : 'middle'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      ),
    },
  ];

  // Custom tooltip for the pie chart
  const renderCustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ fontWeight: 'bold', margin: 0 }}>{data.materiaal_naam}</p>
          <p style={{ margin: '5px 0 0' }}>Bijdrage: {formatPrice(data.verpact_bijdrage)}</p>
          <p style={{ margin: '5px 0 0' }}>{data.percentage.toFixed(1)}% van totaal</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Content style={{ padding: '24px' }}>
        <Spin size="large" />
      </Content>
    );
  }

  return (
    <Content style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Date Range Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px',
        }}>
          <Dropdown 
            overlay={
              <Menu 
                selectedKeys={[activeDateRange]}
                onClick={({ key }) => handleDateRangeChange(key)}
              >
                <Menu.Item key="today">Vandaag</Menu.Item>
                <Menu.Item key="last7Days">Laatste 7 dagen</Menu.Item>
                <Menu.Item key="last30Days">Laatste 30 dagen</Menu.Item>
                <Menu.Item key="thisMonth">Deze maand</Menu.Item>
                <Menu.Item key="lastMonth">Vorige maand</Menu.Item>
              </Menu>
            }
          >
            <Button>
              <Space>
                <CalendarOutlined />
                {activeDateRange === 'today' && 'Vandaag'}
                {activeDateRange === 'last7Days' && 'Laatste 7 dagen'}
                {activeDateRange === 'last30Days' && 'Laatste 30 dagen'}
                {activeDateRange === 'thisMonth' && 'Deze maand'}
                {activeDateRange === 'lastMonth' && 'Vorige maand'}
                {activeDateRange === 'custom' && 'Aangepast bereik'}
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
          
          <RangePicker
            value={dateRange}
            onChange={handleCustomDateRange}
            format="DD-MM-YYYY"
            allowClear={false}
          />
          
          <Button 
            icon={<ReloadOutlined />} 
            onClick={resetDateRange}
            title="Reset naar laatste 30 dagen"
          />
        </div>
        
        {/* Metrics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              }}
            >
              <Statistic
                title={<span style={{ color: '#001350', fontSize: '16px', fontWeight: 500 }}>Totaal orders</span>}
                value={metrics.totalOrders}
                valueStyle={{ color: '#1890ff', fontSize: '24px', fontWeight: 600 }}
                prefix={<ShoppingCartOutlined style={{ color: '#1890ff' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              }}
            >
              <Statistic
                title={<span style={{ color: '#001350', fontSize: '16px', fontWeight: 500 }}>Totale waarde</span>}
                value={metrics.totalRevenue}
                precision={2}
                valueStyle={{ color: '#52c41a', fontSize: '24px', fontWeight: 600 }}
                prefix={<EuroOutlined style={{ color: '#52c41a' }} />}
                formatter={(value) => 
                  new Intl.NumberFormat('nl-NL', { 
                    style: 'decimal',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(Number(value))
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              }}
            >
              <Statistic
                title={<span style={{ color: '#001350', fontSize: '16px', fontWeight: 500 }}>Actieve orders</span>}
                value={metrics.activeOrders}
                valueStyle={{ color: '#FF5d00', fontSize: '24px', fontWeight: 600 }}
                prefix={<ClockCircleOutlined style={{ color: '#FF5d00' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              }}
            >
              <Statistic
                title={<span style={{ color: '#001350', fontSize: '16px', fontWeight: 500 }}>Totaal producten</span>}
                value={metrics.totalProducts}
                valueStyle={{ color: '#722ed1', fontSize: '24px', fontWeight: 600 }}
                prefix={<InboxOutlined style={{ color: '#722ed1' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Order Trend Graph */}
        <Card
          style={{
            borderRadius: '8px',
            marginTop: '16px',
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
            }}>
              <Title level={4} style={{ margin: 0 }}>Orderoverzicht</Title>
            </div>
            
            <div style={{ height: 350, marginBottom: 24, overflow: 'hidden' }}>
              <Card style={{ boxShadow: 'none' }}>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart
                    data={dailyStats}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 10,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      height={10}
                      interval="preserveStartEnd"
                      tickFormatter={(value, index) => {
                        // Only show every 3rd date, plus first and last
                        if (index === 0 || index === dailyStats.length - 1 || index % 3 === 0) {
                          return value;
                        }
                        return '';
                      }}
                    />
                    <YAxis 
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      width={30}
                    />
                    <RechartsTooltip 
                      formatter={(value, name, props) => {
                        return props.payload.tooltipText;
                      }}
                      labelFormatter={(label) => `Datum: ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#1890ff"
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#1890ff', stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#1890ff', stroke: '#fff', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </div>
          </Space>
        </Card>

        {/* Verpact Bijdrage Section */}
        <Card 
          style={{
            borderRadius: '8px',
          }}
        >
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>Verpact Bijdrage</Title>
            <div>
              <Tag color="blue" style={{ fontSize: 16, padding: '4px 8px' }}>
                Totaal: {formatPrice(totalVerpactBijdrage)}
              </Tag>
            </div>
          </div>
          
          {verpactLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </div>
          ) : verpactData.length > 0 ? (
            <div style={{ height: 400, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={verpactData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={isMobile ? 80 : 130}
                    fill="#8884d8"
                    dataKey="verpact_bijdrage"
                    nameKey="materiaal_naam"
                    label={({ materiaal_naam, percentage }) => `${materiaal_naam}: ${percentage.toFixed(1)}%`}
                  >
                    {verpactData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <RechartsTooltip content={renderCustomTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                Geen Verpact gegevens beschikbaar voor uw producten.
              </Typography.Text>
            </div>
          )}
        </Card>

        {/* Order Details Modal */}
        <Modal
          title="Order Details"
          open={!!selectedOrder}
          onCancel={() => setSelectedOrder(null)}
          footer={null}
          width={800}
        >
          {selectedOrder && (
            <Descriptions column={2}>
              <Descriptions.Item label="Order ID">{selectedOrder.id}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColors[selectedOrder.status as keyof typeof statusColors] || 'default'}>
                  {statusLabels[selectedOrder.status as keyof typeof statusLabels] || selectedOrder.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Datum">
                {new Date(selectedOrder.aangemaakt_op).toLocaleString('nl-NL')}
              </Descriptions.Item>
              <Descriptions.Item label="Totaal bedrag">
                {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(selectedOrder.totaal_bedrag)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </Space>
    </Content>
  );
}
